import { useCallback, useEffect, useRef, useState } from 'react';

export type ScrollHint = 'none' | 'down' | 'up';

/**
 * Tracks whether a scroll container has content beyond its visible edges and
 * exposes a jump-to-edge action. Pair with `<ScrollHintButton>` rendered as a
 * sibling of the container inside a `relative` wrapper.
 *
 * @example
 * const { scrollRef, scrollHint, onScrollEdgesUpdate, onScrollJump } = useScrollHint();
 *
 * <div className="relative">
 *   <div ref={scrollRef} onScroll={onScrollEdgesUpdate} className="overflow-y-auto">
 *     ...
 *   </div>
 *   <ScrollHintButton hint={scrollHint} onJump={onScrollJump} />
 * </div>
 *
 * @returns `scrollRef` — callback ref for the scroll container; re-measures on
 *   resize via ResizeObserver, so containers mounted conditionally (after a
 *   loading state) are picked up too.
 * @returns `scrollHint` — `'down'` / `'up'` when content overflows in that
 *   direction (`'down'` wins when both apply), `'none'` when nothing to scroll.
 * @returns `onScrollEdgesUpdate` — attach as the container's `onScroll` handler
 *   to keep the hint in sync while scrolling.
 * @returns `onScrollJump` — smooth-scrolls to the bottom, or back to the top
 *   when already there.
 */
export const useScrollHint = () => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [scrollHint, setScrollHint] = useState<ScrollHint>('none');

  const onScrollEdgesUpdate = useCallback(() => {
    const scrollContainer = elementRef.current;

    if (!scrollContainer) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

    const canScrollDown = scrollTop + clientHeight < scrollHeight - 1;
    const canScrollUp = scrollTop > 0;
    const direction = canScrollDown ? 'down' : 'up';

    setScrollHint(canScrollDown || canScrollUp ? direction : 'none');
  }, []);

  const scrollRef = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      elementRef.current = element;

      if (element) {
        const observer = new ResizeObserver(onScrollEdgesUpdate);
        observer.observe(element);
        observerRef.current = observer;
      }
    },
    [onScrollEdgesUpdate],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const onScrollJump = useCallback(() => {
    const scrollContainer = elementRef.current;

    if (!scrollContainer) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    scrollContainer.scrollTo({
      top: isAtBottom ? 0 : scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  return { scrollRef, scrollHint, onScrollEdgesUpdate, onScrollJump };
};
